import { useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  HouseNightlyTripGlobalFieldFormProps,
  HouseNightlyTripGlobalFormContainer,
  HouseNightlyTripGlobalFormContainerProps,
} from './components/form-container/HouseNightlyTripGlobalFormContainer'
import { HouseNightlyTripGlobalIsEnabledField } from './components/is-enabled-field/HouseNightlyTripGlobalIsEnabledField'
import { HouseNightlyTripGlobalOptionsFields } from './components/options-fields/HouseNightlyTripGlobalOptionsFields'
import {
  computeHouseNightlyTripGlobalDefaultValues,
  computeHouseNightlyTripGlobalInputs,
} from './helpers/compute-house-nightly-trip-global'
import { HouseNightlyTripGlobalDto } from '../../../../../../lib/graphql'
import { HouseOptionsFormLayoutProps } from '../../../HouseOptionsForm'

export type HouseNightlyTripGlobalFormProps = {
  updateNightlyTripGlobal: {
    mutate: (formData: HouseNightlyTripGlobalDto) => void
    isLoading: boolean
  }
}
export const HouseNightlyTripGlobalForm = ({
  handleOnExpand,
  house,
  isDisabled,
  isOpen,
  layoutProps,
  updateNightlyTripGlobal,
}: HouseNightlyTripGlobalFormProps & HouseOptionsFormLayoutProps) => {
  const [hasRequestBeenSent, setHasRequestBeenSent] = useState<boolean>(false)

  const { currencyArea, options } = house

  // Infer GQL Union type
  if (options.__typename !== 'HouseOptionsGlobal') {
    return null
  }

  const { nightlyTrip } = options

  const methods = useForm<HouseNightlyTripGlobalFieldFormProps>({
    defaultValues: computeHouseNightlyTripGlobalDefaultValues(nightlyTrip),
  })
  const {
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
  } = methods

  const { mutate } = updateNightlyTripGlobal

  const isEnabled = watch('isEnabled')

  const timeOutRequest = () => {
    setHasRequestBeenSent(true)
    setTimeout(() => setHasRequestBeenSent(false), 2000)
  }

  const onSubmit = (formData: HouseNightlyTripGlobalFieldFormProps) => {
    const inputs = computeHouseNightlyTripGlobalInputs({ ...formData })

    mutate({ ...inputs })

    reset(formData)

    timeOutRequest()
  }

  const isFieldDisabled = isDisabled || hasRequestBeenSent

  const formContainerProps: Omit<
    HouseNightlyTripGlobalFormContainerProps,
    'children'
  > = {
    handleOnExpand,
    isOpen,
    layoutProps,
    submitButtons: {
      confirm: hasRequestBeenSent,
      disabled: isFieldDisabled || !isDirty || !isValid,
      isLoading: isSubmitting || updateNightlyTripGlobal.isLoading,
      onSubmit: handleSubmit(onSubmit),
      isValid,
    },
  }

  return (
    <FormProvider {...methods}>
      <HouseNightlyTripGlobalFormContainer {...formContainerProps}>
        <HouseNightlyTripGlobalIsEnabledField
          control={control}
          isFieldDisabled={isFieldDisabled}
        />

        {isEnabled ? (
          <HouseNightlyTripGlobalOptionsFields
            control={control}
            currencyArea={currencyArea}
            isFieldDisabled={isFieldDisabled}
          />
        ) : null}
      </HouseNightlyTripGlobalFormContainer>
    </FormProvider>
  )
}
